import { useEffect, useState } from 'react';

import Box from '~/common/components/Box';

import {
  StyledBOLTLogo,
  StyledLoadingStatus,
  StyledSalesBlurb,
  StyledScaleProgressBar,
} from './StyledComponents/AdrAdeProgressBar.styles.js';

// Define the interface for props
interface AdrAdeProgressBarProps {
  displayPercentage: number;
  currentLoadingStatus?: string;
  blurbText: string | string[];
  isComplete?: boolean;
}

const AdrAdeProgressBar = ({
  displayPercentage,
  currentLoadingStatus = null,
  blurbText,
  isComplete = false,
}: Readonly<AdrAdeProgressBarProps>) => {
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [displayedStatus, setDisplayedStatus] = useState(currentLoadingStatus);
  const [transitioningStatus, setTransitioningStatus] = useState<string | null>(
    null,
  );
  const [currentBlurbIndex, setCurrentBlurbIndex] = useState<number | null>(0);
  const isCarousel = Array.isArray(blurbText);

  useEffect(() => {
    if (currentLoadingStatus !== displayedStatus) {
      // Start the fade-out animation
      setIsFadingOut(true);
      setTransitioningStatus(currentLoadingStatus); // Hold the new status during the transition

      // Wait for the fade-out animation to complete
      const fadeOutTimeout = setTimeout(() => {
        setDisplayedStatus(currentLoadingStatus); // Update the displayed status
        setIsFadingOut(false); // Trigger fade-in
        setTransitioningStatus(null); // Clear the transitioning status
      }, 500); // Match the duration of the fade-out animation

      return () => {
        clearTimeout(fadeOutTimeout);
      };
    }
  }, [currentLoadingStatus, displayedStatus]);

  useEffect(() => {
    if (isComplete || !isCarousel) {
      return; // Exit early if isComplete is true
    }

    const interval = setInterval(() => {
      setCurrentBlurbIndex((prevIndex) => (prevIndex + 1) % blurbText.length);
    }, 5 * 1_000);

    return () => clearInterval(interval);
  }, [blurbText.length, isCarousel, isComplete]);

  const roundedPercentage = Math.round(displayPercentage * 100);

  if (isComplete) {
    return null; // Do not render anything if isComplete is true
  }

  return (
    <>
      <StyledBOLTLogo
        src="/images/bolt-logo-fallback.svg"
        alt="BOLT Logo"
        aria-label="BOLT Logo"
        width="186px"
        height="126px"
        isComplete={!isComplete}
        data-is-complete={!isComplete}
      />
      <Box
        component="section"
        aria-labelledby="loadingProgress"
        aria-valuenow={roundedPercentage}
        role="progressbar"
      >
        <Box
          component="section"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mb: '0.25rem',
            fontSize: '0.875rem',
            fontWeight: '500',
          }}
        >
          {isCarousel ? (
            <StyledLoadingStatus isFadingOut={isFadingOut}>
              {transitioningStatus || displayedStatus}
            </StyledLoadingStatus>
          ) : (
            <Box sx={{ flex: 1 }} /> // Placeholder to maintain alignment
          )}
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end' }}
            id="loadingProgress"
          >
            {roundedPercentage}% Completed
          </Box>
        </Box>

        <Box
          sx={{
            backgroundColor: 'gray.200',
            width: '428px',
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          {!isComplete && (
            <StyledScaleProgressBar
              percentage={displayPercentage}
              isVisible={!isComplete}
            />
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
          position: 'relative',
          height: '3.5rem',
          width: '100%',
          marginTop: '1rem',
        }}
      >
        <StyledSalesBlurb
          key={currentBlurbIndex}
          isCarousel={isCarousel}
          isComplete={isComplete}
        >
          {isCarousel ? blurbText[currentBlurbIndex] : blurbText}
        </StyledSalesBlurb>
      </Box>
    </>
  );
};

export default AdrAdeProgressBar;
